import { useDispatch, useSelector } from 'store/store'
import { formatToPTBRNumber, numberToDecimalString } from 'commons/utils/number'
import {
  HeadItemType,
  Icon,
  ImageViewer,
  LoadingHavaianas,
  Modal,
  ModalPreviewPdf,
  Pagination,
  SlimButton,
  Table,
  Tooltip,
  Typography
} from 'components'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getNDDetails } from 'services/manage_ov'
import {
  DadosJustificativa,
  OvMaterialItem,
  ReturnDetalhesND
} from 'services/manage_ov/interface'
import { Negotiation } from 'store/NegotiationsContext/Types/NegotiationType'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { ActionPerm } from 'components/Structures/Header/constants'
import { usePermissions } from 'commons/customHooks/useAuth'
import { OPERATION_STATE } from '../NDTable'
import { base64toFile } from 'commons/utils/object'
import { ActionTabs, ItemActionTabsType } from 'components/Structures/ActionTabs'
import { getOvBinds } from 'store/OVContext/ovSlice'

import * as S from './styles'
import { formatDate } from 'commons/utils/date'
import { theme } from 'styles/theme/hav-theme'
import { OVTypes, YesNo } from 'constants/enums'

interface ModalNDDetailsProps {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  ovNumber: string
  ovType: string
  handleConfirmation: (operation: OPERATION_STATE, ovNumber?: Negotiation) => void
  showCheck: boolean
  isDirector: boolean
  negotiation: Negotiation
}
interface MaterialSize {
  CodigoTamanho: string
  QuantidadeItensPares: number
}

const ModalNDDetails = ({
  isOpen = false,
  setIsOpen,
  ovNumber,
  ovType,
  showCheck,
  isDirector,
  negotiation,
  handleConfirmation
}: ModalNDDetailsProps) => {
  const PAGE_SIZE = 10
  const dispatch = useDispatch()
  const [ndDetails, setNdDetails] = useState<ReturnDetalhesND>({
    DadosJustificativa: [] as DadosJustificativa[]
  } as ReturnDetalhesND)
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [totalItems, setTotalItems] = useState(0)
  const [openModalPreviewPdf, setOpenModalPreviewPdf] = useState(false)
  const [openImageViewer, setOpenImageViewer] = useState(false)
  const [hideModal, setHideModal] = useState(false)
  const [currentModalTab, setCurrentModalTab] = useState('0')
  const [searchResults, setSearchResults] = useState<ReturnDetalhesND>(
    {} as ReturnDetalhesND
  )

  const { loadingBind, bindOvsList, bindTotalizers } = useSelector((state) => state.ov)
  const { verifyPermission } = usePermissions()
  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.ND_MANAGE, action)
  }

  useEffect(() => {
    if (ovType === OVTypes.YRBA) dispatch(getOvBinds(ovNumber))
  }, [])
  useEffect(() => {
    getMaterials()
  }, [currentPage])
  useEffect(() => {
    setSearchResults(ndDetails)
  }, [ndDetails])

  const indexJustified = ndDetails?.DadosJustificativa?.length - 1 || 0
  const nomeDoArquivo = ndDetails?.DadosJustificativa[indexJustified]?.NomeDoArquivo
  const arquivo = ndDetails?.DadosJustificativa[indexJustified]?.Arquivo

  const getMaterials = async () => {
    setLoading(true)

    try {
      const response = await getNDDetails(
        { Paginas: currentPage - 1, Linhas: PAGE_SIZE },
        `${ovNumber}`
      )

      const updatedMaterials: OvMaterialItem[] = response.data.DetalhesMateriais.map(
        (material) => ({
          ...material,
          statusAsNumber: Number(material.Status.split('-')[0]),
          checked: false
        })
      )
      setNdDetails({
        ...response.data,
        DetalhesMateriais: updatedMaterials
      })
      setTotalItems(response?.data?.RowsCount)
      setTotalPages(Math.ceil(response?.data?.RowsCount / PAGE_SIZE))
    } catch (error) {
      toast.error('Erro ao carregar materiais')
    } finally {
      setLoading(false)
    }
  }

  const getMaterialSizes = (): HeadItemType[] => {
    const sizes: string[] = []

    if (!loading && ndDetails && ndDetails.DetalhesMateriais.length) {
      ndDetails.DetalhesMateriais.forEach((item: OvMaterialItem) => {
        if (item.TamanhosMaterial?.length) {
          item.TamanhosMaterial.forEach((size: MaterialSize) => {
            if (!sizes.includes(size.CodigoTamanho)) {
              sizes.push(size.CodigoTamanho)
            }
          })
        }
      })

      sizes.sort()
    }

    return sizes.map((size) => ({
      label: size,
      columnName: size
    }))
  }

  const getMaterialsHead = () => {
    const sizes = getMaterialSizes()

    const tableHead: HeadItemType[] = [
      { label: 'Material', columnName: 'Material' },
      { label: '', columnName: '' },
      { label: 'Ds. material', columnName: 'DsMaterial' },
      { label: 'Cor', columnName: 'Cor' },
      { label: 'Ds. cor', columnName: 'DsCor' },
      { label: 'Qtd. cor', columnName: 'QtdCor' },
      { label: 'R$ cor', columnName: 'PrecoCor' },
      { label: 'Desc. devido', columnName: 'DescDevido' },
      { label: 'Desc. aplicado', columnName: 'DescAplicado' },
      { label: 'Desc. excedente', columnName: 'DescExcedente' },
      { label: 'Desc. canal', columnName: 'DescCanal' },
      { label: 'Desc. cliente', columnName: 'DescCliente' },
      { label: 'Desc. material/cor', columnName: 'DescMaterialCor' },
      ...sizes
    ]

    return tableHead
  }

  const getMaterialsData = () => {
    const tableData = []
    if (searchResults) {
      const materialsData = searchResults?.DetalhesMateriais?.map(
        (item: OvMaterialItem) => {
          const sizesRow = getMaterialSizes().map((size) => {
            const foundSize = item.TamanhosMaterial?.find(
              (itemSize: MaterialSize) => itemSize.CodigoTamanho === size.label
            )

            return foundSize ? (
              foundSize.QuantidadeItensPares
            ) : (
              <S.EmptySize>0</S.EmptySize>
            )
          })

          const hasExtraDiscount = item.ValorDescontoAplicado > item.ValorDescontoDevido

          return {
            id: `${item.NumeroItem}`,
            checked: item.checked,
            onDoubleClick: () => {
              return
            },
            content: [
              <Tooltip
                arrow
                placement="top"
                key={`tooltip-${item.CodigoMaterial}-${item.CodigoCor}`}
                title={
                  <Typography design="hav-default-03-text">
                    Preço Unitário: {formatToPTBRNumber(item.ValorUnitario)}
                  </Typography>
                }
              >
                <span>{item.CodigoMaterial}</span>
              </Tooltip>,
              item.FlagNegociacaoDiferenciada === YesNo.YES ? (
                <S.IconWrapper>
                  <Icon icon="nd" color={theme.colors.alert.warning[40]} size="25" />
                </S.IconWrapper>
              ) : (
                ''
              ),
              <Tooltip
                arrow
                placement="top"
                key={`tooltip-desc-${item.CodigoMaterial}-${item.CodigoCor}`}
                title={
                  <Typography design="hav-default-03-text">
                    {item.DescricaoMaterial}
                  </Typography>
                }
              >
                <S.EllipsisBox maxWidth={150}>
                  <span>{item.DescricaoMaterial}</span>
                </S.EllipsisBox>
              </Tooltip>,
              item.CodigoCor,
              item.DescricaoCor,
              item.QuantidadeItem,
              formatToPTBRNumber(item.ValorTotalCor),
              <div key="tbody-8">
                <strong>{item.PercentualDescontoDevido}%</strong> •{' '}
                {formatToPTBRNumber(item.ValorDescontoDevido)}
              </div>,
              hasExtraDiscount ? (
                <S.WarningText>
                  <strong>{item.PercentualDescontoAplicado}%</strong> •{' '}
                  {formatToPTBRNumber(item.ValorDescontoAplicado)}
                </S.WarningText>
              ) : (
                <>
                  <strong>{item.PercentualDescontoAplicado}%</strong> •{' '}
                  {formatToPTBRNumber(item.ValorDescontoAplicado)}
                </>
              ),
              <div key="tbody-10">
                <strong>{item.PercentualDescontoExcedente}%</strong> •{' '}
                {formatToPTBRNumber(item.ValorDescontoExcedente)}
              </div>,
              hasExtraDiscount ? (
                <S.WarningText>
                  <strong>{item.PercentualDescontoCanal}%</strong>
                </S.WarningText>
              ) : (
                `${item.PercentualDescontoCanal}%`
              ),
              hasExtraDiscount ? (
                <S.WarningText>
                  <strong>{item.PercentualDescontoCliente}%</strong>
                </S.WarningText>
              ) : (
                `${item.PercentualDescontoCliente}%`
              ),
              hasExtraDiscount ? (
                <S.WarningText>
                  <strong>{item.PercentualDescontoMaterialCor}%</strong>
                </S.WarningText>
              ) : (
                `${item.PercentualDescontoMaterialCor}%`
              ),
              ...sizesRow
            ]
          }
        }
      )

      tableData.push(...materialsData)
    }

    return tableData
  }

  const MaterialsTable = () => {
    return (
      <S.TableWrapper>
        <Table
          rowHeight={32}
          headData={getMaterialsHead()}
          bodyData={getMaterialsData()}
          iconColumns={[1]}
        />
        <S.PaginationWrapper>
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            numberOfPages={totalPages}
            numberOfItems={totalItems}
            itemsPerPage={PAGE_SIZE}
          />
          <S.SummaryWrapper>
            <S.TotalizerContainer>
              <S.TotalizerItem>
                <Typography design="hav-default-02-text">Qtd. Total</Typography>
                <span>{ndDetails.DetalhesMateriais[0].QuantidadeTotalOrdemVenda}</span>
              </S.TotalizerItem>
              <S.TotalizerItem>
                <Typography design="hav-default-02-text">Valor referência</Typography>
                <span>
                  {formatToPTBRNumber(
                    ndDetails.DetalhesMateriais[0].ValorTotalOrdemVenda
                  )}
                </span>
              </S.TotalizerItem>
              <S.TotalizerItem>
                <Typography design="hav-default-02-text">Valor total</Typography>
                <span>
                  {formatToPTBRNumber(
                    ndDetails.DetalhesMateriais[0].ValorTotalOrdemVendaSap
                  )}
                </span>
              </S.TotalizerItem>
              <S.TotalizerItem>
                <Typography design="hav-default-02-text">Valor total desc.</Typography>
                <span>
                  {formatToPTBRNumber(ndDetails.DetalhesMateriais[0].ValorTotalDesconto)}
                </span>
              </S.TotalizerItem>
            </S.TotalizerContainer>
          </S.SummaryWrapper>
        </S.PaginationWrapper>
      </S.TableWrapper>
    )
  }

  const bindTableHead: HeadItemType[] = [
    { label: 'Data da OV', columnName: 'DataOV' },
    { label: 'Mês cota', columnName: 'MesCota' },
    { label: 'OV Portal', columnName: 'OVPortal' },
    { label: 'OV SAP', columnName: 'OVSAP' },
    { label: 'Fábrica', columnName: 'Fabrica' },
    { label: 'Qtd.', columnName: 'Quantidade' },
    { label: 'Valor (R$)', columnName: 'Valor' },
    { label: 'Prev. Faturamento', columnName: 'PrevFaturamento' },
    { label: 'Prev. Entrega', columnName: 'PrevEntrega' }
  ]

  const bindTableData = () => {
    return bindOvsList.map((ov) => {
      return {
        id: ov.NumeroOrdemVenda,
        content: [
          ov.DataDigitacao,
          formatDate(ov.MesCota, 'MM/YYYY', 'YYYYMM'),
          ov.NumeroOrdemVenda,
          ov.NumeroOrdemVendaSap,
          ov.CodigoCentroMaterial,
          numberToDecimalString(ov.QuantidadeTotalItem),
          formatToPTBRNumber(ov.ValorTotalItem),
          ov.DataPrevistaFaturamento,
          ov.DataPrevistaEntrega
        ]
      }
    })
  }

  const BindTable = () => {
    return (
      <S.TableWrapper>
        <Table headData={bindTableHead} rowHeight={40} bodyData={bindTableData()} />
        <S.TotalizerWrapper>
          <S.TotalizerContainer>
            <S.TotalizerItem>
              <Typography design="hav-h7-text">Qtd. total</Typography>
              <span>{numberToDecimalString(bindTotalizers.QuantidadeTotal)}</span>
            </S.TotalizerItem>
            <S.TotalizerItem>
              <Typography design="hav-h7-text">Valor total</Typography>
              <span>{formatToPTBRNumber(bindTotalizers.ValorTotal)}</span>
            </S.TotalizerItem>
          </S.TotalizerContainer>
        </S.TotalizerWrapper>
      </S.TableWrapper>
    )
  }

  const isPdfFile = (filename: string) => {
    const verify = filename.split('.')
    return verify[verify.length - 1] === 'pdf'
  }

  const imageExtensions = ['jpg', 'jpeg', 'png']

  const isImageFile = (filename: string) => {
    const verify = filename.split('.')
    const extension = verify[verify.length - 1]
    return imageExtensions.includes(extension)
  }

  const parseUploadFile = () => {
    if (arquivo && nomeDoArquivo) {
      const parsedFile = base64toFile(arquivo, nomeDoArquivo)

      return parsedFile
    }

    return new File([], '')
  }
  const handleDownload = () => {
    toast.success('Seu download iniciará em instantes')

    // TODO: Replace with utils/downlodFile({file, fileName, fileType})
    const url = window.URL.createObjectURL(parseUploadFile())
    const link = document.createElement('a')
    link.href = url

    if (nomeDoArquivo) {
      link.setAttribute('download', nomeDoArquivo)
      link.click()
      return
    }

    link.setAttribute('download', 'arquivo-de-justificativa')
    link.click()
  }

  const handlePreview = () => {
    if (nomeDoArquivo) {
      if (isPdfFile(nomeDoArquivo)) previewPdf()
      else if (isImageFile(nomeDoArquivo)) imageViewer()
    }
  }
  const imageViewer = () => {
    setOpenImageViewer(!openImageViewer)
    setHideModal(!hideModal)
  }
  const previewPdf = () => {
    setOpenModalPreviewPdf(!openModalPreviewPdf)
    setHideModal(!hideModal)
  }

  const NDModalTabs: ItemActionTabsType[] = [
    {
      label: `Detalhes da OV ${OVTypes.YRBA}`,
      onClick: () => setCurrentModalTab('0')
    },
    {
      label: 'OVs vinculadas',
      onClick: () => setCurrentModalTab('1')
    }
  ]

  return (
    <>
      {!hideModal && (
        <Modal
          open={isOpen}
          setOpen={setIsOpen}
          showAction={false}
          title={
            <S.SmallRowWrapper>
              <S.Title>ND pendente • </S.Title> <S.SubTitle>{ovNumber}</S.SubTitle>{' '}
              {showCheck && (
                <Tooltip title="Aprovada por GEV" placement="top" arrow>
                  <S.ApprovalStatus>
                    <Icon icon="task-alt" color={theme.colors.primary.main} size="20" />
                  </S.ApprovalStatus>
                </Tooltip>
              )}
            </S.SmallRowWrapper>
          }
          showClose
        >
          {loading || loadingBind ? (
            <LoadingHavaianas />
          ) : (
            <S.RowWrapper>
              {ovType === OVTypes.YRBA ? (
                <S.TabsWrapper>
                  <ActionTabs items={NDModalTabs} currentItem={currentModalTab} />
                  {currentModalTab === '0' && <MaterialsTable />}
                  {currentModalTab === '1' && <BindTable />}
                </S.TabsWrapper>
              ) : (
                <S.BigWrapper>
                  <MaterialsTable />
                </S.BigWrapper>
              )}
              <S.SmallWrapper>
                <S.TitleBox>
                  <Typography design="hav-h7-text">Dados para análise</Typography>
                </S.TitleBox>
                <S.RowWrapper>
                  <S.DataItem>
                    <S.DataTitle>Executivo</S.DataTitle>
                    <S.DataValue>
                      {ndDetails?.DetalhesOrdemVenda?.RegiaoVenda?.substring(0, 6)}
                    </S.DataValue>
                  </S.DataItem>
                  <S.DataItem>
                    <S.DataTitle>Nome Executivo</S.DataTitle>
                    <Tooltip
                      arrow
                      placement="top"
                      title={ndDetails?.DetalhesOrdemVenda?.RegiaoVenda?.substring(9)}
                    >
                      <S.DataValue>
                        <S.EllipsisBox maxWidth={150}>
                          {ndDetails?.DetalhesOrdemVenda?.RegiaoVenda?.substring(9)}
                        </S.EllipsisBox>
                      </S.DataValue>
                    </Tooltip>
                  </S.DataItem>
                </S.RowWrapper>
                <S.RowWrapper>
                  <S.DataItem>
                    <S.DataTitle>SAP cliente</S.DataTitle>
                    <S.DataValue>
                      {ndDetails?.DetalhesOrdemVenda?.CodigoSapCliente}
                    </S.DataValue>
                  </S.DataItem>
                  <S.DataItem>
                    <S.DataTitle>Razão social</S.DataTitle>
                    <Tooltip
                      arrow
                      placement="top"
                      title={ndDetails?.DetalhesOrdemVenda?.RazaoSocial}
                    >
                      <S.DataValue>
                        <S.EllipsisBox maxWidth={150}>
                          {ndDetails?.DetalhesOrdemVenda?.RazaoSocial}
                        </S.EllipsisBox>
                      </S.DataValue>
                    </Tooltip>
                  </S.DataItem>
                </S.RowWrapper>
                <S.RowWrapper>
                  <S.DataItem>
                    <S.DataTitle>Solicitação</S.DataTitle>
                    <S.DataValue>{formatDate(negotiation.DataSolicitacao)}</S.DataValue>
                  </S.DataItem>
                  <S.DataItem>
                    <S.DataTitle>Aprovador</S.DataTitle>
                    <S.DataValue>
                      {negotiation.Aprovador ? negotiation.Aprovador : '-'}
                    </S.DataValue>
                  </S.DataItem>
                </S.RowWrapper>
                <S.RowWrapper>
                  <S.DataItem>
                    <S.DataTitle>Desconto (%)</S.DataTitle>
                    <S.DataValue>
                      {ndDetails?.DetalhesOrdemVenda?.PercentualDescontoDevido !==
                      undefined
                        ? `${ndDetails?.DetalhesOrdemVenda?.PercentualDescontoAplicado} (devido -> ${ndDetails?.DetalhesOrdemVenda?.PercentualDescontoDevido})`
                        : ''}
                    </S.DataValue>
                  </S.DataItem>
                  <S.DataItem>
                    <S.DataTitle>Prazo (dias)</S.DataTitle>
                    <S.DataValue>
                      {ndDetails?.DetalhesOrdemVenda?.PrazoDevido !== undefined
                        ? `${ndDetails?.DetalhesOrdemVenda?.PrazoAplicado} (devido -> ${ndDetails?.DetalhesOrdemVenda?.PrazoDevido})`
                        : ''}
                    </S.DataValue>
                  </S.DataItem>
                </S.RowWrapper>
                <S.DataItem>
                  <S.DataTitle>Motivo</S.DataTitle>
                  <S.DataValue>
                    {ndDetails?.DadosJustificativa[0].DescricaoMotivoNegociacao}
                  </S.DataValue>
                </S.DataItem>
                <S.DataItem>
                  <S.DataTitle>Descrição</S.DataTitle>
                  <S.DataDescription>
                    {ndDetails?.DadosJustificativa[0].DescricaoDaJustificativa}
                  </S.DataDescription>
                </S.DataItem>
                <S.DataItem>
                  <S.DataTitle>Tipo de OV</S.DataTitle>
                  <S.DataValue>{ovType}</S.DataValue>
                </S.DataItem>
                <S.DataItem>
                  <S.DataTitle>Anexo</S.DataTitle>
                  {nomeDoArquivo ? (
                    <S.SmallRowWrapper>
                      <S.DataValue>{nomeDoArquivo}</S.DataValue>
                      <SlimButton
                        onClick={handlePreview}
                        variant="icon"
                        icon={<Icon icon="visibility" size="20" />}
                      />
                      <SlimButton
                        onClick={handleDownload}
                        variant="icon"
                        icon={<Icon icon="download" size="20" />}
                      />
                    </S.SmallRowWrapper>
                  ) : (
                    <S.DataValue>Nenhum</S.DataValue>
                  )}
                </S.DataItem>
                {showCheck && !isDirector ? (
                  <S.RowWrapper>
                    <Tooltip
                      title="Aguardando aprovação da diretoria"
                      placement="top"
                      arrow
                    >
                      {/** TODO: Replace with SlimButton */}
                      <S.InfoButton>
                        <Icon icon="clock" color={theme.colors.neutral.white} size="15" />{' '}
                        Avaliação pendente
                      </S.InfoButton>
                    </Tooltip>
                  </S.RowWrapper>
                ) : (
                  <S.RowWrapper>
                    {/** TODO: Replace with SlimButton */}
                    <S.RejectButton
                      disabled={shouldDisable(ACTIONS_SCREENS.DISMISS)}
                      onClick={() =>
                        handleConfirmation(OPERATION_STATE.REJECT, negotiation)
                      }
                    >
                      <Icon icon="cancel" color={theme.colors.neutral.white} size="15" />{' '}
                      Reprovar
                    </S.RejectButton>
                    {/** TODO: Replace with SlimButton */}
                    <S.ApproveButton
                      disabled={shouldDisable(ACTIONS_SCREENS.APPROVE)}
                      onClick={() =>
                        handleConfirmation(OPERATION_STATE.APPROVE, negotiation)
                      }
                    >
                      <Icon
                        icon="task-alt"
                        color={theme.colors.neutral.white}
                        size="15"
                      />{' '}
                      Aprovar
                    </S.ApproveButton>
                  </S.RowWrapper>
                )}
              </S.SmallWrapper>
            </S.RowWrapper>
          )}
        </Modal>
      )}
      <ModalPreviewPdf
        title={
          <AnexoTitle showCheck={showCheck} ovNumber={ovNumber} click={previewPdf} />
        }
        open={openModalPreviewPdf}
        setOpen={previewPdf}
        fileUrl={window.URL.createObjectURL(parseUploadFile())}
      />

      <ImageViewer
        title={
          <AnexoTitle showCheck={showCheck} ovNumber={ovNumber} click={imageViewer} />
        }
        open={openImageViewer}
        setOpen={imageViewer}
        fileUrl={window.URL.createObjectURL(parseUploadFile())}
      />
    </>
  )
}

export default ModalNDDetails

type AnexoTitleProps = {
  showCheck: boolean
  ovNumber: string
  click: () => void
}
const AnexoTitle = ({ showCheck, ovNumber, click }: AnexoTitleProps) => {
  return (
    <S.SmallRowWrapper>
      <SlimButton
        id="btn-material-return"
        onClick={click}
        variant="icon"
        icon={<Icon icon="chevron-left" size="25" />}
      />
      <S.Title>Anexo • </S.Title> <S.SubTitle>{ovNumber}</S.SubTitle>{' '}
      {showCheck && (
        <Tooltip title="Aprovada por GEV" placement="top" arrow>
          <S.ApprovalStatus>
            <Icon icon="task-alt" color={theme.colors.primary.main} size="20" />
          </S.ApprovalStatus>
        </Tooltip>
      )}
    </S.SmallRowWrapper>
  )
}
